import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    Dumbbell,
    Plus,
    Search,
    Filter,
    Play,
    Pause,
    Edit,
    Trash2,
    X,
    Upload,
    Loader2,
    ChevronDown,
    Tag,
} from 'lucide-react';

interface Exercise {
    id: string;
    name: string;
    description: string | null;
    instructions: string | null;
    category: string;
    muscle_groups: string[];
    equipment: string[];
    difficulty: string;
    video_url: string | null;
    thumbnail_url: string | null;
    is_global: boolean;
    is_active: boolean;
    created_at: string;
}

const CATEGORIES = [
    'Peito', 'Costas', 'Ombros', 'Bíceps', 'Tríceps',
    'Antebraço', 'Core', 'Quadríceps', 'Posterior', 'Glúteos',
    'Panturrilha', 'Cardio', 'Funcional', 'Alongamento'
];

const MUSCLE_GROUPS = [
    'Peitoral Maior', 'Peitoral Menor', 'Deltóide Anterior', 'Deltóide Lateral',
    'Deltóide Posterior', 'Trapézio', 'Latíssimo', 'Rombóides', 'Bíceps',
    'Tríceps', 'Braquial', 'Reto Abdominal', 'Oblíquos', 'Lombar',
    'Quadríceps', 'Isquiotibiais', 'Glúteo Máximo', 'Glúteo Médio',
    'Adutores', 'Abdutores', 'Panturrilha'
];

const EQUIPMENT = [
    'Barra', 'Halteres', 'Máquina', 'Cabos', 'Kettlebell',
    'Peso Corporal', 'Faixas Elásticas', 'Bola Suíça', 'TRX',
    'Medicine Ball', 'Battle Rope', 'Step', 'Banco'
];

const DIFFICULTY = ['Iniciante', 'Intermediário', 'Avançado'];

export function ExercisesPage() {
    const { tenant } = useAuth();
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
    const [showFilters, setShowFilters] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingExercise, setEditingExercise] = useState<Exercise | null>(null);
    const [playingVideo, setPlayingVideo] = useState<string | null>(null);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        instructions: '',
        category: '',
        muscle_groups: [] as string[],
        equipment: [] as string[],
        difficulty: 'Intermediário',
        video_url: '',
    });

    // Fetch exercises
    const { data: exercises, isLoading } = useQuery({
        queryKey: ['exercises', tenant?.id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('exercises')
                .select('*')
                .or(`is_global.eq.true,tenant_id.eq.${tenant?.id}`)
                .eq('is_active', true)
                .order('name');

            if (error) throw error;
            return data as Exercise[];
        },
        enabled: !!tenant?.id,
    });

    // Create/Update exercise
    const saveMutation = useMutation({
        mutationFn: async (data: typeof formData & { id?: string }) => {
            if (data.id) {
                // Update
                const { error } = await supabase
                    .from('exercises')
                    .update({
                        name: data.name,
                        description: data.description || null,
                        instructions: data.instructions || null,
                        category: data.category,
                        muscle_groups: data.muscle_groups,
                        equipment: data.equipment,
                        difficulty: data.difficulty,
                        video_url: data.video_url || null,
                    })
                    .eq('id', data.id);

                if (error) throw error;
            } else {
                // Create
                const { error } = await supabase.from('exercises').insert({
                    tenant_id: tenant?.id,
                    name: data.name,
                    description: data.description || null,
                    instructions: data.instructions || null,
                    category: data.category,
                    muscle_groups: data.muscle_groups,
                    equipment: data.equipment,
                    difficulty: data.difficulty,
                    video_url: data.video_url || null,
                    is_global: false,
                    is_active: true,
                });

                if (error) throw error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['exercises'] });
            closeModal();
        },
    });

    // Delete exercise
    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            const { error } = await supabase
                .from('exercises')
                .update({ is_active: false })
                .eq('id', id);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['exercises'] });
        },
    });

    const closeModal = () => {
        setShowModal(false);
        setEditingExercise(null);
        setFormData({
            name: '',
            description: '',
            instructions: '',
            category: '',
            muscle_groups: [],
            equipment: [],
            difficulty: 'Intermediário',
            video_url: '',
        });
    };

    const openEditModal = (exercise: Exercise) => {
        setEditingExercise(exercise);
        setFormData({
            name: exercise.name,
            description: exercise.description || '',
            instructions: exercise.instructions || '',
            category: exercise.category,
            muscle_groups: exercise.muscle_groups || [],
            equipment: exercise.equipment || [],
            difficulty: exercise.difficulty,
            video_url: exercise.video_url || '',
        });
        setShowModal(true);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        saveMutation.mutate({
            ...formData,
            id: editingExercise?.id,
        });
    };

    const toggleMultiSelect = (
        arr: string[],
        value: string,
        setter: (arr: string[]) => void
    ) => {
        if (arr.includes(value)) {
            setter(arr.filter((v) => v !== value));
        } else {
            setter([...arr, value]);
        }
    };

    // Filter exercises
    const filteredExercises = exercises?.filter((ex) => {
        const matchesSearch =
            ex.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            ex.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = !categoryFilter || ex.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    // Group by category
    const groupedExercises = filteredExercises?.reduce((acc, ex) => {
        if (!acc[ex.category]) acc[ex.category] = [];
        acc[ex.category].push(ex);
        return acc;
    }, {} as Record<string, Exercise[]>);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="section-title flex items-center gap-2">
                        <Dumbbell className="w-6 h-6" />
                        Biblioteca de Exercícios
                    </h1>
                    <p className="section-subtitle">
                        {exercises?.length} exercícios disponíveis
                    </p>
                </div>
                <button onClick={() => setShowModal(true)} className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Exercício
                </button>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar exercícios..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="input pl-10"
                    />
                </div>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`btn-secondary ${showFilters ? 'bg-primary-50 text-primary-700' : ''}`}
                >
                    <Filter className="w-4 h-4 mr-2" />
                    Filtros
                    <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </button>
            </div>

            {/* Filter chips */}
            {showFilters && (
                <div className="card">
                    <p className="text-sm font-medium text-gray-700 mb-3">Categorias</p>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => setCategoryFilter(null)}
                            className={`badge ${!categoryFilter ? 'badge-primary' : 'badge-secondary'}`}
                        >
                            Todas
                        </button>
                        {CATEGORIES.map((cat) => (
                            <button
                                key={cat}
                                onClick={() => setCategoryFilter(cat)}
                                className={`badge ${categoryFilter === cat ? 'badge-primary' : 'badge-secondary'}`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Exercise Grid */}
            {groupedExercises && Object.keys(groupedExercises).length > 0 ? (
                <div className="space-y-8">
                    {Object.entries(groupedExercises).map(([category, exs]) => (
                        <div key={category}>
                            <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Tag className="w-4 h-4 text-primary-600" />
                                {category} ({exs.length})
                            </h2>
                            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {exs.map((exercise) => (
                                    <ExerciseCard
                                        key={exercise.id}
                                        exercise={exercise}
                                        isPlaying={playingVideo === exercise.id}
                                        onPlay={() => setPlayingVideo(playingVideo === exercise.id ? null : exercise.id)}
                                        onEdit={() => openEditModal(exercise)}
                                        onDelete={() => {
                                            if (confirm('Tem certeza que deseja excluir este exercício?')) {
                                                deleteMutation.mutate(exercise.id);
                                            }
                                        }}
                                        canEdit={!exercise.is_global}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="card text-center py-12">
                    <Dumbbell className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <h3 className="font-medium text-gray-900 mb-2">Nenhum exercício encontrado</h3>
                    <p className="text-gray-500 mb-4">Adicione exercícios à sua biblioteca</p>
                    <button onClick={() => setShowModal(true)} className="btn-primary">
                        <Plus className="w-4 h-4 mr-2" />
                        Criar Exercício
                    </button>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 my-8">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold">
                                {editingExercise ? 'Editar Exercício' : 'Novo Exercício'}
                            </h2>
                            <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Name */}
                            <div>
                                <label className="label">Nome do Exercício *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Ex: Supino Reto com Barra"
                                    className="input"
                                    required
                                />
                            </div>

                            {/* Category */}
                            <div>
                                <label className="label">Categoria *</label>
                                <select
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className="input"
                                    required
                                >
                                    <option value="">Selecione...</option>
                                    {CATEGORIES.map((cat) => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Muscle Groups */}
                            <div>
                                <label className="label">Grupos Musculares</label>
                                <div className="flex flex-wrap gap-2 p-3 border rounded-lg max-h-32 overflow-y-auto">
                                    {MUSCLE_GROUPS.map((muscle) => (
                                        <button
                                            key={muscle}
                                            type="button"
                                            onClick={() =>
                                                toggleMultiSelect(
                                                    formData.muscle_groups,
                                                    muscle,
                                                    (arr) => setFormData({ ...formData, muscle_groups: arr })
                                                )
                                            }
                                            className={`badge text-xs ${formData.muscle_groups.includes(muscle)
                                                ? 'badge-primary'
                                                : 'badge-secondary'
                                                }`}
                                        >
                                            {muscle}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Equipment */}
                            <div>
                                <label className="label">Equipamentos</label>
                                <div className="flex flex-wrap gap-2 p-3 border rounded-lg">
                                    {EQUIPMENT.map((eq) => (
                                        <button
                                            key={eq}
                                            type="button"
                                            onClick={() =>
                                                toggleMultiSelect(
                                                    formData.equipment,
                                                    eq,
                                                    (arr) => setFormData({ ...formData, equipment: arr })
                                                )
                                            }
                                            className={`badge text-xs ${formData.equipment.includes(eq)
                                                ? 'badge-primary'
                                                : 'badge-secondary'
                                                }`}
                                        >
                                            {eq}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Difficulty */}
                            <div>
                                <label className="label">Dificuldade</label>
                                <div className="flex gap-2">
                                    {DIFFICULTY.map((d) => (
                                        <button
                                            key={d}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, difficulty: d })}
                                            className={`btn ${formData.difficulty === d ? 'btn-primary' : 'btn-secondary'
                                                } flex-1`}
                                        >
                                            {d}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="label">Descrição</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Descrição breve do exercício..."
                                    className="input min-h-[80px]"
                                    rows={3}
                                />
                            </div>

                            {/* Instructions */}
                            <div>
                                <label className="label">Instruções de Execução</label>
                                <textarea
                                    value={formData.instructions}
                                    onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                                    placeholder="Passo a passo da execução correta..."
                                    className="input min-h-[100px]"
                                    rows={4}
                                />
                            </div>

                            {/* Video URL */}
                            <div>
                                <label className="label">URL do Vídeo (YouTube)</label>
                                <input
                                    type="url"
                                    value={formData.video_url}
                                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                                    placeholder="https://youtube.com/watch?v=..."
                                    className="input"
                                />
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 pt-4">
                                <button type="button" onClick={closeModal} className="btn-secondary flex-1">
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={saveMutation.isPending}
                                    className="btn-primary flex-1"
                                >
                                    {saveMutation.isPending ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : null}
                                    {editingExercise ? 'Salvar Alterações' : 'Criar Exercício'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

// Exercise Card Component
function ExerciseCard({
    exercise,
    isPlaying,
    onPlay,
    onEdit,
    onDelete,
    canEdit,
}: {
    exercise: Exercise;
    isPlaying: boolean;
    onPlay: () => void;
    onEdit: () => void;
    onDelete: () => void;
    canEdit: boolean;
}) {
    const getYouTubeId = (url: string) => {
        const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
        return match ? match[1] : null;
    };

    const videoId = exercise.video_url ? getYouTubeId(exercise.video_url) : null;

    return (
        <div className="card-hover overflow-hidden">
            {/* Video/Thumbnail */}
            <div className="relative aspect-video bg-gray-100 -mx-6 -mt-6 mb-4">
                {isPlaying && videoId ? (
                    <iframe
                        src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                        className="absolute inset-0 w-full h-full"
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                    />
                ) : (
                    <>
                        {videoId ? (
                            <img
                                src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
                                alt={exercise.name}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                        ) : (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <Dumbbell className="w-12 h-12 text-gray-300" />
                            </div>
                        )}
                        {videoId && (
                            <button
                                onClick={onPlay}
                                className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                            >
                                <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                                    <Play className="w-6 h-6 text-gray-900 ml-1" />
                                </div>
                            </button>
                        )}
                    </>
                )}

                {/* Global badge */}
                {exercise.is_global && (
                    <div className="absolute top-2 left-2 badge badge-secondary text-xs">
                        Global
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">{exercise.name}</h3>

                {exercise.description && (
                    <p className="text-sm text-gray-500 line-clamp-2">{exercise.description}</p>
                )}

                <div className="flex flex-wrap gap-1">
                    {exercise.muscle_groups?.slice(0, 3).map((muscle) => (
                        <span key={muscle} className="badge badge-secondary text-xs">
                            {muscle}
                        </span>
                    ))}
                    {(exercise.muscle_groups?.length || 0) > 3 && (
                        <span className="badge badge-secondary text-xs">
                            +{(exercise.muscle_groups?.length || 0) - 3}
                        </span>
                    )}
                </div>
            </div>

            {/* Actions */}
            {canEdit && (
                <div className="flex gap-2 mt-4 pt-4 border-t">
                    <button onClick={onEdit} className="btn-ghost btn-sm flex-1">
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                    </button>
                    <button onClick={onDelete} className="btn-ghost btn-sm text-red-600 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            )}
        </div>
    );
}
